import React, { useMemo } from 'react';
import MonthlyDistributionChart from '../../placeholders/MonthlyDistributionChart';
import {
  addMonths,
  endOfMonth,
  getQuincenasInRange,
  parseDateSafe,
  startOfMonth,
} from '../../placeholders/periodSummaryUtils';
import {
  buildPoPaymentScheduleRows,
  normalizePoItemType,
  PO_ITEM_TYPE_STACK_DIMENSION,
  PO_PAYMENT_CHART_STACK_DIMENSION,
  poLineAmountClp,
  resolvePoPaymentChartGroup,
} from '../../placeholders/purchaseOrderSummary';

const QUINCENA_AXIS_MODES = [{ key: 'month', label: 'Quincena' }];

const PAYMENT_CHART_METRICS = [
  { key: 'amount', label: 'Monto', totalLabel: 'CLP' },
  { key: 'count', label: 'Cantidad', totalLabel: 'OC' },
];

const ITEM_TYPE_CHART_METRICS = [
  { key: 'amount', label: 'Monto', totalLabel: 'CLP' },
  { key: 'count', label: 'Cantidad', totalLabel: 'líneas' },
];

const resolveQuincenaRange = (dates) => {
  const sorted = [...(dates || [])].filter(Boolean).sort((a, b) => a - b);
  const today = new Date();
  if (!sorted.length) {
    return {
      start: startOfMonth(addMonths(today, -3)),
      end: endOfMonth(addMonths(today, 3)),
    };
  }
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const start = startOfMonth(min < addMonths(today, -6) ? addMonths(today, -6) : min);
  const endCandidate = max > addMonths(today, 6) ? max : addMonths(today, 3);
  const end = endOfMonth(endCandidate < start ? start : endCandidate);
  return { start, end };
};

/**
 * Gráficos de pagos: quincenas (proyección por estado + pagadas) y tipo de compra.
 */
const FinancePurchasePaymentsCharts = ({ orders = [], orderItems = [] }) => {
  const schedule = useMemo(() => buildPoPaymentScheduleRows(orders), [orders]);

  const scheduleByPoId = useMemo(() => {
    const map = new Map();
    schedule.forEach((row) => {
      const id = Number(row.id);
      if (id) map.set(id, row);
    });
    return map;
  }, [schedule]);

  const paymentChartItems = useMemo(
    () =>
      schedule
        .filter((row) => row.dueIso)
        .map((row) => ({
          date: row.dueIso,
          paymentGroup: resolvePoPaymentChartGroup(row),
          purchaseOrderId: Number(row.id),
          amount: Number(row.payment_amount || 0),
          id: row.id,
        })),
    [schedule]
  );

  const itemTypeChartItems = useMemo(() => {
    const linesSumByPo = new Map();
    (orderItems || []).forEach((item) => {
      const poId = Number(item.purchase_order_id);
      if (!poId) return;
      const line = Number(item.line_total || 0);
      if (!Number.isFinite(line) || line <= 0) return;
      linesSumByPo.set(poId, (linesSumByPo.get(poId) || 0) + line);
    });

    return (orderItems || [])
      .map((item) => {
        const poId = Number(item.purchase_order_id);
        const scheduleRow = scheduleByPoId.get(poId);
        if (!scheduleRow?.dueIso) return null;
        const order = scheduleRow;
        return {
          date: scheduleRow.dueIso,
          itemType: normalizePoItemType(item.item_type),
          purchaseOrderId: poId,
          amount: poLineAmountClp({ ...item, po_lines_sum: linesSumByPo.get(poId) || 0 }, order),
          id: item.id,
        };
      })
      .filter(Boolean);
  }, [orderItems, scheduleByPoId]);

  const chartPeriods = useMemo(() => {
    const dates = [
      ...paymentChartItems.map((item) => parseDateSafe(item.date)),
      ...itemTypeChartItems.map((item) => parseDateSafe(item.date)),
    ].filter(Boolean);
    const range = resolveQuincenaRange(dates);
    return getQuincenasInRange(range.start, range.end);
  }, [paymentChartItems, itemTypeChartItems]);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 12,
        alignItems: 'start',
        padding: '12px 12px 4px',
      }}
    >
      <MonthlyDistributionChart
        periods={chartPeriods}
        items={paymentChartItems}
        title="Pagos pagados y proyectados"
        totalLabel="CLP"
        emptyMessage="Sin vencimientos de pago para mostrar."
        stackDimensions={[PO_PAYMENT_CHART_STACK_DIMENSION]}
        defaultStackKey="paymentGroup"
        valueMetrics={PAYMENT_CHART_METRICS}
        defaultMetricKey="amount"
        axisModes={QUINCENA_AXIS_MODES}
        defaultAxisKey="month"
      />
      <MonthlyDistributionChart
        periods={chartPeriods}
        items={itemTypeChartItems}
        title="Distribución por tipo de compra"
        totalLabel="CLP"
        emptyMessage="Sin ítems de compra con vencimiento."
        stackDimensions={[PO_ITEM_TYPE_STACK_DIMENSION]}
        defaultStackKey="itemType"
        valueMetrics={ITEM_TYPE_CHART_METRICS}
        defaultMetricKey="amount"
        axisModes={QUINCENA_AXIS_MODES}
        defaultAxisKey="month"
      />
    </div>
  );
};

export default FinancePurchasePaymentsCharts;
